import {Component, DestroyRef, inject, OnInit, Output, ViewChild} from '@angular/core';
import {of, Observable, BehaviorSubject} from 'rxjs';
import {take, switchMap, filter, map, withLatestFrom} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import {TreeNode} from '@ali-hm/angular-tree-component';
import {MenuAttributesComponent} from '../menu-attributes/menu-attributes.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {DialogService} from '@emm/shared/services/dialog.service';
import {MenuFiltersFacadeService} from '@emm/menu/services/menu-filters-facade.service';
import {MenuFormFacadeService} from '@emm/menu/services/menu-form-facade.service';
import {MenuFacadeService} from '@emm/menu/services/menu-facade.service';
import {MenuTreeFacadeService} from '@emm/menu/services/menu-tree-facade.service';
import {FeatureScope} from '@emm/core/util/feature-scope.constants';
import {Consumer} from '@emm/core/models/consumer.model';
import {MatDialog} from '@angular/material/dialog';
import {UserService} from '@emm/core/services/user.service';

@Component({
    selector: 'emm-menu',
    styleUrls: ['./menu.component.scss'],
    templateUrl: './menu.component.html',
    standalone: false
})
export class MenuComponent implements OnInit {

    isReadOnly$ = this.authFacade.isReadOnly$.pipe(map((isReadOnly) => isReadOnly || this.userService.isMmsAuthorOnly()));
    prevNode: TreeNode;
    selectedFeatureScope$ = this.treeFacade.selectedFeatureScope$;

    @Output() submenuRenamed = new BehaviorSubject<boolean>(false);
    @ViewChild(MenuAttributesComponent) menuForm: MenuAttributesComponent;

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    constructor(
        private authFacade: AuthFacadeService,
        private dialogService: DialogService,
        private filtersFacade: MenuFiltersFacadeService,
        private formFacade: MenuFormFacadeService,
        private menuService: MenuFacadeService,
        private treeFacade: MenuTreeFacadeService,
        private dialog: MatDialog,
        private userService: UserService,
    ) {
    }

    ngOnInit(): void {
        this.menuService.init();
    }


    onSelectItem(payload: {
        prevNode: TreeNode;
        currentNode: TreeNode,
        isSearchMode: boolean,
        isReadOnly: boolean,
        itemAlreadyLoaded?: boolean,
    }): void {
        if (RA.isNotNil(payload)) {
            const {prevNode, currentNode, isSearchMode, isReadOnly, itemAlreadyLoaded} = payload;
            let scope;

            this.selectedFeatureScope$.pipe(take(1)).subscribe(featureScope => scope = featureScope);

            this.checkIfItemHasChanges(prevNode)
                .pipe(
                    take(1),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe((_) => {
                    this.prevNode = currentNode;
                    if (!itemAlreadyLoaded) {
                        currentNode.setIsActive(true);
                    }

                    if (payload.isSearchMode) {
                        this.treeFacade.selectSearchItem(currentNode.data);
                        this.formFacade.selectTreeNode.next(currentNode);
                    } else {
                        if (!itemAlreadyLoaded) {
                            this.treeFacade.selectItem(currentNode.data.data);
                        }
                        this.formFacade.selectTreeNode.next(currentNode);
                    }
                });
        } else {
            this.prevNode = null;
        }
    }


    checkIfItemHasChanges(prevNode: TreeNode): Observable<boolean> {
        return this.isReadOnly$.pipe(
            take(1),
            withLatestFrom(this.selectedFeatureScope$),
            switchMap(([isReadOnly, featureScope]) => {
                if (this.hasChanges() && !isReadOnly && RA.isNotNil(prevNode)
                    && (featureScope === FeatureScope.Global)) {
                    return this.dialogService
                        .openSaveChangesDialog(prevNode.data.data.name)
                        .afterClosed()
                        .pipe(
                            switchMap((res) => {
                                if (res) {
                                    this.menuForm.attributesForm.submit(false);
                                    return this.formFacade.updateMenuItem$ as Observable<boolean>;
                                } else {
                                    this.menuForm.attributesForm.cancel();
                                    return of(true);
                                }
                            })
                        );
                }
                return of(true);
            })
        );
    }


    hasChanges(): boolean {
        if (this.menuForm.attributesForm && this.menuForm.attributesForm.dynamicForms) {
            this.menuForm.attributesForm.dynamicForms.some(df => df.form.dirty)
        }
        return false
    }


    onDeleteConsumer(consumer: Consumer): void {
        this.checkIfItemHasChanges(this.prevNode)
            .pipe(
                take(1),
                switchMap(() =>
                    this.dialogService
                        .openConfirmDialog('Are you sure you want to delete', consumer.name)
                        .afterClosed()
                        .pipe(filter(RA.isTrue), map(() => consumer.tag))
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((tag) => this.filtersFacade.deleteConsumer(tag));
    }


    onSubmenuRenamed($event: boolean) {
        this.submenuRenamed.next($event);
    }
}
